import { useParams, useHistory, useLocation } from 'react-router-dom';
import { useSelector, useDispatch } from 'react-redux';
import { translate, useTranslate } from 'hooks/useTranslate';
import {
  FC,
  ReactNode,
  createContext,
  useContext,
  useState,
  useEffect,
  useMemo,
  useCallback,
} from 'react';

import { State } from 'redux/initialState';
import { useGetActualSociety } from 'hooks/useGetActualSociety';
import { useGetMenu } from 'modules/administratives/hooks/useGetMenu';
import { differenceInCalendarDays, startOfDay } from 'date-fns';

import menuTypes from 'constants/menuTypes';
import sizes from 'constants/sizes';

import { Society } from 'types/Society';
import {
  BeneficiaryAgreedIncentiveOffer,
  IncentiveOffer,
  IncentiveOfferOperation,
  IncentiveOfferPriceContext,
  IncentiveOfferSellerInfo,
  IncentiveOfferStatus,
  IncentiveOfferTimelineStep,
  IncentiveOfferType,
  IncentivesMarketBeneficiary,
} from 'modules/incentivesMarket/types';
import { setModal } from 'modules/_shared/redux/modalActions';
import {
  getBeneficiary,
  getBeneficiaries,
  getBeneficiaryIncentivesMarketContext,
} from 'modules/incentivesMarket/api/beneficiaryActions';
import { getIncentiveOfferBeneficiaryPublicInfo } from 'modules/incentivesMarket/api/getIncentiveOfferBeneficiaryPublicInfo';
import { getIncentiveOfferEligibles } from 'modules/incentivesMarket/api/getIncentiveOfferEligibles';
import { IncentiveOfferSellerOfferCap } from 'modules/incentivesMarket/types/IncentivesMarketBeneficiaryContext';
import { Eligible } from 'modules/transactions/pages/TenderOfferDetails/types/Eligible';
import { store } from 'redux/store';
import errorSwal from 'utils/errorSwal';

import { IncentiveOfferDetailContextType } from '../types/IncentiveOfferDetailContextType';
import { incentiveOfferDetailInitValue } from '../constants/IncentiveOfferDetailInitValue';
import { getDefaultTimelineSteps } from '../constants/incentiveOfferTimelineSteps';
import { getIncentiveOfferOperations } from '../../../redux/incentiveOfferOperationActions';
import { getIncentiveOffer } from '../../../redux/incentiveOfferActions';
import AcceptIncentiveOfferModal from '../../../modals/AcceptIncentiveOffer';
import { AcceptIncentiveOfferModalProvider } from '../../../modals/AcceptIncentiveOffer/context/AcceptIncentiveOfferModalContext';
import GenerateIncentiveOfferPurchaseContractModal from '../../../modals/GenerateIncentiveOfferPurchaseContractModal';
import { GenerateIncentiveOfferPurchaseContractModalProvider } from '../../../modals/GenerateIncentiveOfferPurchaseContractModal/context/GenerateIncentiveOfferPurchaseContractModalContext';
import getOfferReference from '../../../utils/getOfferReference';
import { getOfferStatus } from '../../../utils/getOfferStatus';
import { getPurchaseQuantityBounds } from '../../../utils/getPurchaseQuantityBounds';
import { useGetBeneficiaryIncentivesMarketContext } from '../../../hooks/useGetBeneficiaryIncentivesMarketContext';
import { buildSellerOfferCapFromContext } from '../../../utils/getSellerLimitsSummary';
import { SellSelection } from '../types/SellUnitsSelection';
import { mapIncentiveOfferEligiblesToTableRows } from '../utils/mapIncentiveOfferEligiblesToTableRows';
import { downloadIncentiveOfferEligiblesXlsx } from '../utils/downloadIncentiveOfferEligiblesXlsx';
import { downloadIncentiveOfferOperationsXlsx } from '../utils/downloadIncentiveOfferOperationsXlsx';

const COMMISSION_RATE = 0.015;
const DEFAULT_ELIGIBLES_ROWS_PER_PAGE = 10;
const DEFAULT_OPERATIONS_ROWS_PER_PAGE = 10;

const DEFAULT_OPERATIONS_SUMMARY = {
  completed: 0,
  pending: 0,
  totalUnits: 0,
  totalAmount: 0,
};

export const IncentiveOfferDetailContext =
  createContext<IncentiveOfferDetailContextType>(incentiveOfferDetailInitValue);

export const IncentiveOfferDetailProvider: FC<{
  children: ReactNode;
}> = ({ children }) => {
  const { societyId, incentiveOfferId } = useParams<{
    societyId: string;
    incentiveOfferId: string;
  }>();
  const { t } = useTranslate();
  const history = useHistory();
  const dispatch = useDispatch();

  const location = useLocation();
  const queryParams = new URLSearchParams(location.search);
  const beneficiaryId = queryParams.get('beneficiaryId');
  const beneficiaryQueryParam = beneficiaryId
    ? `?beneficiaryId=${beneficiaryId}`
    : '';

  const actualSociety: Society = useSelector(
    (state: State) => state.society?.actualSociety
  );
  const user = useSelector((state: State) => state.user);
  const isAdmin = useSelector(
    (state: State) => state.society?.role?.isAdmin ?? false
  );

  const [userBeneficiaryIds, setUserBeneficiaryIds] = useState<string[]>([]);

  const [beneficiaryBuyer, setBeneficiaryBuyer] =
    useState<IncentivesMarketBeneficiary | null>(null);
  const [buyerPlansLabel, setBuyerPlansLabel] = useState<string>('');

  const [offer, setOffer] = useState<IncentiveOffer | null>(null);
  const [acceptanceHistory, setAcceptanceHistory] = useState<
    BeneficiaryAgreedIncentiveOffer[]
  >([]);
  const [priceContext, setPriceContext] = useState<IncentiveOfferPriceContext>({
    currentOfferPrice: 0,
    lastTransactionPrice: 0,
    avgSellOffersPrice: 0,
    officialValuation: 0,
    userAvgBuyPrice: 0,
  });
  const [sellerInfo, setSellerInfo] = useState<IncentiveOfferSellerInfo | null>(
    null
  );
  const [incentiveOfferOperations, setIncentiveOfferOperations] = useState<
    IncentiveOfferOperation[]
  >([]);
  const [purchaseQuantity, setPurchaseQuantity] = useState<number>(0);
  const [sellPlanSelection, setSellPlanSelection] = useState<
    Record<string, number> | null
  >(null);
  const [isLoading, setIsLoading] = useState<boolean>(true);
  const [eligibles, setEligibles] = useState<Eligible[]>([]);
  const [eligiblesPage, setEligiblesPage] = useState(1);
  const [eligiblesRowsPerPage, setEligiblesRowsPerPage] = useState(
    DEFAULT_ELIGIBLES_ROWS_PER_PAGE
  );
  const [eligiblesTotal, setEligiblesTotal] = useState(0);
  const [eligiblesSearchTerm, setEligiblesSearchTerm] = useState('');
  const [isLoadingEligibles, setIsLoadingEligibles] = useState<boolean>(false);
  const [isDownloadingEligibles, setIsDownloadingEligibles] =
    useState<boolean>(false);
  const [isDownloadingOperations, setIsDownloadingOperations] =
    useState<boolean>(false);
  const [operationsPage, setOperationsPage] = useState(1);
  const [operationsRowsPerPage, setOperationsRowsPerPage] = useState(
    DEFAULT_OPERATIONS_ROWS_PER_PAGE
  );
  const [operationsTotal, setOperationsTotal] = useState(0);
  const [operationsSummary, setOperationsSummary] = useState(
    DEFAULT_OPERATIONS_SUMMARY
  );
  const [isLoadingOperations, setIsLoadingOperations] = useState<boolean>(false);
  const [hasParticipatedInOffer, setHasParticipatedInOffer] =
    useState<boolean>(false);
  const [existingBeneficiaryOperation, setExistingBeneficiaryOperation] =
    useState<IncentiveOfferOperation | null>(null);
  const [isLoadingParticipationCheck, setIsLoadingParticipationCheck] =
    useState<boolean>(false);

  useGetActualSociety(societyId);
  useGetMenu(menuTypes.NULL);

  const resolvedSocietyId = actualSociety?._id || societyId || '';

  const offerStatus = useMemo((): IncentiveOfferStatus => {
    if (!offer) return IncentiveOfferStatus.ACTIVE;
    return getOfferStatus(offer);
  }, [offer]);

  const isExpired = offerStatus === IncentiveOfferStatus.EXPIRED;

  const isOwnOffer = useMemo(() => {
    if (!offer) return false;
    const publisherId =
      typeof offer.beneficiary === 'string'
        ? offer.beneficiary
        : offer.beneficiary?._id;
    const publisherIdStr = publisherId ? String(publisherId) : '';

    if (beneficiaryId && publisherIdStr === beneficiaryId) {
      return true;
    }

    return Boolean(
      publisherIdStr && userBeneficiaryIds.includes(publisherIdStr)
    );
  }, [offer, beneficiaryId, userBeneficiaryIds]);

  const isBuyOffer = offer?.type === IncentiveOfferType.BUY;
  const resolvedSellerBeneficiaryId =
    beneficiaryBuyer?._id || beneficiaryId || '';
  const shouldLoadSellerCap =
    isBuyOffer &&
    !isOwnOffer &&
    Boolean(resolvedSellerBeneficiaryId) &&
    Boolean(resolvedSocietyId) &&
    Boolean(actualSociety?._id);

  const { data: sellerMarketContext, isLoading: isLoadingSellerCap } =
    useGetBeneficiaryIncentivesMarketContext({
      societyId: resolvedSocietyId,
      beneficiaryId: resolvedSellerBeneficiaryId,
      incentiveOfferId,
      enabled: shouldLoadSellerCap,
    });

  const offerReference = useMemo(() => getOfferReference(offer), [offer]);

  const offerRemainingUnits = useMemo(() => {
    if (!offer) return 0;
    return Math.max(0, offer.unitsCount.total - offer.unitsCount.agreed);
  }, [offer]);

  const sellerOfferCap = useMemo(():
    | IncentiveOfferSellerOfferCap
    | undefined => {
    if (!isBuyOffer || isOwnOffer) return undefined;

    return buildSellerOfferCapFromContext(
      sellerMarketContext.sellerOfferCap,
      offerRemainingUnits,
      sellerMarketContext.totalAvailableUnits
    );
  }, [
    isBuyOffer,
    isOwnOffer,
    sellerMarketContext.sellerOfferCap,
    sellerMarketContext.totalAvailableUnits,
    offerRemainingUnits,
  ]);

  const sellerPlans = useMemo(
    () => sellerMarketContext.plans ?? [],
    [sellerMarketContext.plans]
  );

  const availableShares = useMemo(() => {
    if (!offer) return 0;

    if (isBuyOffer && !isOwnOffer && sellerOfferCap) {
      return sellerOfferCap.effectiveMax;
    }

    return offerRemainingUnits;
  }, [offer, isBuyOffer, isOwnOffer, sellerOfferCap, offerRemainingUnits]);

  const daysRemaining = useMemo(() => {
    if (!offer) return 0;
    const days = differenceInCalendarDays(
      startOfDay(new Date(offer.endDate)),
      startOfDay(new Date())
    );
    return Math.max(0, days);
  }, [offer]);

  const timelineSteps = useMemo((): IncentiveOfferTimelineStep[] => {
    if (!offer) return getDefaultTimelineSteps();

    const steps = getDefaultTimelineSteps();
    const agreedCount = offer.unitsCount.agreed;
    const approvedCount = offer.unitsCount.approved;
    const totalCount = offer.unitsCount.total;

    // Step 1: Published - always completed if offer exists
    steps[0].isCompleted = true;

    // Step 2: Waiting acceptance - completed if any shares agreed
    steps[1].isCompleted = agreedCount > 0;
    steps[1].isCurrent = agreedCount === 0;

    // Step 3: KYC verification - completed if shares approved
    steps[2].isCompleted = approvedCount > 0;
    steps[2].isCurrent = agreedCount > 0 && approvedCount === 0;

    // Step 4: Society validation - completed if all approved
    steps[3].isCompleted = approvedCount === totalCount && totalCount > 0;
    steps[3].isCurrent = approvedCount > 0 && approvedCount < totalCount;

    // Step 5: Transfer - completed if fully accepted
    steps[4].isCompleted = offerStatus === IncentiveOfferStatus.ACCEPTED;
    steps[4].isCurrent =
      approvedCount === totalCount &&
      offerStatus !== IncentiveOfferStatus.ACCEPTED;

    return steps;
  }, [offer, offerStatus]);

  const handleGoBack = useCallback(() => {
    history.goBack();
  }, [history]);

  const handlePurchaseQuantityChange = useCallback(
    (quantity: number) => {
      const { min, max } = getPurchaseQuantityBounds(offer, availableShares);

      if (max <= 0) {
        setPurchaseQuantity(0);
        return;
      }

      const clampedQuantity = Math.max(min, Math.min(quantity, max));
      setPurchaseQuantity(clampedQuantity);
    },
    [availableShares, offer]
  );

  useEffect(() => {
    const { min, max } = getPurchaseQuantityBounds(offer, availableShares);

    setPurchaseQuantity((prev) => {
      if (max <= 0) return 0;
      if (offer && !offer.isPartialAmountEnabled) {
        return max;
      }
      if (prev === 0) return 0;
      return Math.max(min, Math.min(prev, max));
    });
  }, [offer, availableShares]);

  const handleBuySharesSuccess = useCallback(() => {
    setOffer((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        unitsCount: {
          ...prev.unitsCount,
          agreed: prev.unitsCount.agreed + purchaseQuantity,
        },
      };
    });

    const resolvedBeneficiaryId = beneficiaryBuyer?._id || beneficiaryId;
    const planId = beneficiaryBuyer?.plan;

    if (!resolvedBeneficiaryId || !planId || !societyId) {
      return;
    }

    history.push(
      `/detalle-beneficiario/${societyId}/${planId}/${resolvedBeneficiaryId}`
    );
  }, [
    purchaseQuantity,
    history,
    societyId,
    beneficiaryBuyer,
    beneficiaryId,
  ]);

  const openAcceptModal = useCallback(
    (options?: {
      quantity?: number;
      planSelection?: Record<string, number> | null;
    }) => {
      if (!societyId || !offer || !user || !beneficiaryBuyer) return;
      if (hasParticipatedInOffer) return;

      const resolvedQuantity = options?.quantity ?? purchaseQuantity;
      const resolvedPlanSelection =
        options?.planSelection !== undefined
          ? options.planSelection
          : sellPlanSelection;

      const realSellerName =
        sellerInfo?.name ||
        (typeof offer.beneficiary === 'object' && offer.beneficiary !== null
          ? offer.beneficiary.name || ''
          : '');

      const sellerName = offer.isSellerNameHidden
        ? t('HiddenName')
        : realSellerName;

      const unitClassName =
        typeof offer.unitClass === 'string' ? '' : offer.unitClass?.name || '';

      dispatch(
        setModal(
          <AcceptIncentiveOfferModalProvider
            offer={offer}
            beneficiaryBuyer={beneficiaryBuyer}
            purchaseQuantity={resolvedQuantity}
            societyId={societyId}
            offerReference={offerReference}
            sellerName={sellerName}
            buyerPlansLabel={buyerPlansLabel || unitClassName}
            sellerPlans={sellerPlans}
            sellPlanSelection={resolvedPlanSelection}
            onSuccess={handleBuySharesSuccess}
            size={sizes.LG}
          >
            <AcceptIncentiveOfferModal />
          </AcceptIncentiveOfferModalProvider>
        )
      );
    },
    [
      offer,
      user,
      beneficiaryBuyer,
      societyId,
      purchaseQuantity,
      sellPlanSelection,
      offerReference,
      dispatch,
      handleBuySharesSuccess,
      buyerPlansLabel,
      sellerPlans,
      sellerInfo,
      t,
      hasParticipatedInOffer,
    ]
  );

  const handleBuyShares = useCallback(() => {
    openAcceptModal();
  }, [openAcceptModal]);

  const handleSellUnitsConfirm = useCallback(
    (selection: SellSelection) => {
      setPurchaseQuantity(selection.totals.units);
      setSellPlanSelection(selection.unitsByPlan);
      openAcceptModal({
        quantity: selection.totals.units,
        planSelection: selection.unitsByPlan,
      });
    },
    [openAcceptModal]
  );

  const handleViewOtherOffers = useCallback(() => {
    history.push(`/ofertas-incentivos/${societyId}${beneficiaryQueryParam}`);
  }, [history, societyId, beneficiaryQueryParam]);

  const handleGoToOperation = useCallback(
    (operationId: string) => {
      history.push(
        `/detalle-operacion-incentivos/${societyId}/${operationId}${beneficiaryQueryParam}`
      );
    },
    [history, societyId, beneficiaryQueryParam]
  );

  const handleRefreshOperations = useCallback(async () => {
    if (beneficiaryId || !incentiveOfferId || !offer) {
      setIncentiveOfferOperations([]);
      setOperationsTotal(0);
      setOperationsSummary(DEFAULT_OPERATIONS_SUMMARY);
      setOperationsPage(1);
      setIsLoadingOperations(false);
      return;
    }

    setIsLoadingOperations(true);

    const page = isAdmin ? operationsPage : 1;
    const items = isAdmin ? operationsRowsPerPage : 1;

    try {
      const response = await store.dispatch(
        getIncentiveOfferOperations({
          incentiveOfferId,
          societyId: resolvedSocietyId,
          page,
          items,
        })
      );

      setIncentiveOfferOperations(response?.data || []);
      setOperationsTotal(response?.total || 0);
      setOperationsSummary(response?.summary || DEFAULT_OPERATIONS_SUMMARY);
    } catch {
      setIncentiveOfferOperations([]);
      setOperationsTotal(0);
      setOperationsSummary(DEFAULT_OPERATIONS_SUMMARY);
    } finally {
      setIsLoadingOperations(false);
    }
  }, [
    beneficiaryId,
    incentiveOfferId,
    offer,
    isAdmin,
    operationsPage,
    operationsRowsPerPage,
    resolvedSocietyId,
  ]);

  const handleOpenGenerateContractModal = useCallback(
    (operation: IncentiveOfferOperation) => {
      if (!isAdmin) return;

      dispatch(
        setModal(
          <GenerateIncentiveOfferPurchaseContractModalProvider
            operation={operation}
            onSuccess={handleRefreshOperations}
            size={sizes.XXL}
          >
            <GenerateIncentiveOfferPurchaseContractModal />
          </GenerateIncentiveOfferPurchaseContractModalProvider>
        )
      );
    },
    [dispatch, isAdmin, handleRefreshOperations]
  );

  const handleEligiblesPageChange = useCallback((page: number) => {
    setEligiblesPage(page);
  }, []);

  const handleEligiblesRowsPerPageChange = useCallback((rowsPerPage: number) => {
    setEligiblesPage(1);
    setEligiblesRowsPerPage(rowsPerPage);
  }, []);

  const handleEligiblesSearchChange = useCallback((searchTerm: string) => {
    setEligiblesSearchTerm(searchTerm);
    setEligiblesPage(1);
  }, []);

  const handleOperationsPageChange = useCallback((page: number) => {
    setOperationsPage(page);
  }, []);

  const handleOperationsRowsPerPageChange = useCallback((rowsPerPage: number) => {
    setOperationsPage(1);
    setOperationsRowsPerPage(rowsPerPage);
  }, []);

  const handleDownloadEligibles = useCallback(async () => {
    if (!resolvedSocietyId || !incentiveOfferId || eligiblesTotal === 0) {
      return;
    }

    try {
      setIsDownloadingEligibles(true);
      await downloadIncentiveOfferEligiblesXlsx({
        incentiveOfferId,
        societyId: resolvedSocietyId,
        offerReference,
        t,
      });
    } catch {
      errorSwal(t('ErrorDownloadingEligibles'));
    } finally {
      setIsDownloadingEligibles(false);
    }
  }, [
    resolvedSocietyId,
    incentiveOfferId,
    eligiblesTotal,
    offerReference,
    t,
  ]);

  const handleDownloadOperations = useCallback(async () => {
    if (
      !resolvedSocietyId ||
      !incentiveOfferId ||
      operationsTotal === 0 ||
      !offer?.type
    ) {
      return;
    }

    try {
      setIsDownloadingOperations(true);
      await downloadIncentiveOfferOperationsXlsx({
        incentiveOfferId,
        societyId: resolvedSocietyId,
        offerReference,
        offerType: offer.type,
        t,
      });
    } catch {
      errorSwal(t('ErrorDownloadingOperations'));
    } finally {
      setIsDownloadingOperations(false);
    }
  }, [
    resolvedSocietyId,
    incentiveOfferId,
    operationsTotal,
    offerReference,
    offer?.type,
    t,
  ]);

  useEffect(() => {
    if (!resolvedSocietyId || !user?._id) return;

    let cancelled = false;

    const fetchBeneficiaryBuyer = async () => {
      const res = await store.dispatch(
        getBeneficiaries(resolvedSocietyId, { userId: user._id, items: 100 })
      );

      if (cancelled) {
        return;
      }

      const beneficiaries: IncentivesMarketBeneficiary[] = res?.data ?? [];
      const fromList = beneficiaries.find(
        (item) => String(item._id) === String(beneficiaryId)
      );

      if (fromList) {
        setBeneficiaryBuyer(fromList);
        return;
      }

      if (beneficiaries.length === 1) {
        setBeneficiaryBuyer(beneficiaries[0]);
        return;
      }

      if (!beneficiaryId) {
        setBeneficiaryBuyer(beneficiaries[0] ?? null);
        return;
      }

      try {
        const loadedBeneficiary: IncentivesMarketBeneficiary | null =
          await store.dispatch(
            getBeneficiary(beneficiaryId, { societyId: resolvedSocietyId })
          );

        if (!cancelled && loadedBeneficiary) {
          setBeneficiaryBuyer(loadedBeneficiary);
        }
      } catch {
        if (!cancelled) {
          setBeneficiaryBuyer(null);
        }
      }
    };

    fetchBeneficiaryBuyer();

    return () => {
      cancelled = true;
    };
  }, [beneficiaryId, resolvedSocietyId, user?._id, isAdmin]);

  useEffect(() => {
    if (!beneficiaryBuyer?._id || !resolvedSocietyId) {
      setBuyerPlansLabel('');
      return;
    }

    let cancelled = false;

    const loadBuyerPlans = async () => {
      try {
        const context = await store.dispatch(
          getBeneficiaryIncentivesMarketContext({
            beneficiaryId: beneficiaryBuyer._id,
            societyId: resolvedSocietyId,
          })
        );
        if (cancelled) return;

        const label =
          context?.plans
            ?.map((plan: { planName?: string }) => plan.planName)
            .filter(Boolean)
            .join(', ') || '';

        setBuyerPlansLabel(label);
      } catch {
        if (!cancelled) {
          setBuyerPlansLabel('');
        }
      }
    };

    loadBuyerPlans();

    return () => {
      cancelled = true;
    };
  }, [beneficiaryBuyer?._id, resolvedSocietyId]);

  useEffect(() => {
    if (!resolvedSocietyId || !user?._id) {
      setUserBeneficiaryIds([]);
      return;
    }

    let cancelled = false;

    const fetchUserPartners = async () => {
      const res = await store.dispatch(
        getBeneficiaries(resolvedSocietyId, { userId: user._id, items: 100 })
      );
      if (cancelled) {
        return;
      }

      let ids =
        res?.data
          ?.map((p: IncentivesMarketBeneficiary) => String(p._id))
          .filter(Boolean) ?? [];

      if (resolvedSellerBeneficiaryId) {
        try {
          const cifContext: { beneficiaryIds?: string[] } =
            await store.dispatch(
              getBeneficiaryIncentivesMarketContext({
                beneficiaryId: resolvedSellerBeneficiaryId,
                societyId: resolvedSocietyId,
              })
            );
          if (cifContext?.beneficiaryIds?.length) {
            ids = [
              ...new Set([...ids, ...cifContext.beneficiaryIds.map(String)]),
            ];
          }
        } catch {
          // keep user-linked ids only
        }
      }

      setUserBeneficiaryIds(ids);
    };

    fetchUserPartners();

    return () => {
      cancelled = true;
    };
  }, [resolvedSocietyId, user?._id, resolvedSellerBeneficiaryId]);

  const actualSocietyId = actualSociety?._id;

  useEffect(() => {
    if (!societyId || !incentiveOfferId || !actualSocietyId) {
      return undefined;
    }

    let cancelled = false;

    const fetchOfferDetail = async () => {
      setIsLoading(true);

      try {
        let currentOffer: IncentiveOffer | null = null;
        try {
          currentOffer = await store.dispatch(
            getIncentiveOffer(incentiveOfferId)
          );
        } catch {
          currentOffer = null;
        }

        if (cancelled) {
          return;
        }

        if (
          currentOffer &&
          String(currentOffer.society) !== String(actualSocietyId)
        ) {
          setOffer(null);
          setAcceptanceHistory([]);
          setIncentiveOfferOperations([]);
          setOperationsTotal(0);
          setOperationsSummary(DEFAULT_OPERATIONS_SUMMARY);
          setSellerInfo(null);
          return;
        }

        setOffer(currentOffer);
        setAcceptanceHistory([]);

        setPriceContext({
          currentOfferPrice: currentOffer?.unitPrice || 0,
          lastTransactionPrice: 0,
          avgSellOffersPrice: 0,
          officialValuation: 0,
          userAvgBuyPrice: 0,
        });

        if (incentiveOfferId) {
          const sellerBeneficiary =
            await getIncentiveOfferBeneficiaryPublicInfo({
              incentiveOfferId,
            });

          if (cancelled) {
            return;
          }

          if (sellerBeneficiary) {
            const beneficiaryShares =
              sellerBeneficiary.sharesCount?.actual ||
              sellerBeneficiary.sharesCount?.future ||
              0;
            const totalSocietyShares = actualSociety?.sharesCount?.actual || 1;

            const isHidden = currentOffer?.isSellerNameHidden === true;

            let memberSince = '';
            const memberSinceDate = sellerBeneficiary.invitationDate;
            if (!isHidden && memberSinceDate) {
              memberSince = new Date(memberSinceDate).getFullYear().toString();
            }

            setSellerInfo({
              beneficiaryId: sellerBeneficiary._id,
              name: isHidden
                ? translate('HiddenName')
                : sellerBeneficiary.name || '',
              image: isHidden ? '' : sellerBeneficiary.image || '',
              memberSince,
              totalShares: beneficiaryShares,
              capitalPercentage:
                totalSocietyShares > 0
                  ? (beneficiaryShares / totalSocietyShares) * 100
                  : 0,
              previousOperations: 0,
              rating: 0,
            });
          } else {
            setSellerInfo(null);
          }
        } else {
          setSellerInfo(null);
        }
      } catch (error) {
        if (!cancelled) {
          console.error('Error fetching trading offer detail:', error);
          setOffer(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoading(false);
        }
      }
    };

    fetchOfferDetail();

    return () => {
      cancelled = true;
    };
  }, [societyId, incentiveOfferId, actualSocietyId]);

  useEffect(() => {
    if (!isAdmin || !resolvedSocietyId || !incentiveOfferId || !offer) {
      setEligibles([]);
      setEligiblesTotal(0);
      setEligiblesPage(1);
      setIsLoadingEligibles(false);
      return undefined;
    }

    let cancelled = false;

    const fetchEligibles = async () => {
      setIsLoadingEligibles(true);

      try {
        const response = await getIncentiveOfferEligibles({
          incentiveOfferId,
          societyId: resolvedSocietyId,
          page: eligiblesPage,
          items: eligiblesRowsPerPage,
          search: eligiblesSearchTerm || undefined,
        });

        if (cancelled) {
          return;
        }

        setEligibles(
          mapIncentiveOfferEligiblesToTableRows({
            items: response?.data || [],
            societyId: resolvedSocietyId,
          })
        );
        setEligiblesTotal(response?.pagination?.totalItems || 0);

        if (
          response?.pagination?.page &&
          response.pagination.page !== eligiblesPage
        ) {
          setEligiblesPage(response.pagination.page);
        }
      } catch {
        if (!cancelled) {
          setEligibles([]);
          setEligiblesTotal(0);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingEligibles(false);
        }
      }
    };

    fetchEligibles();

    return () => {
      cancelled = true;
    };
  }, [
    isAdmin,
    resolvedSocietyId,
    incentiveOfferId,
    offer?._id,
    eligiblesPage,
    eligiblesRowsPerPage,
    eligiblesSearchTerm,
  ]);

  useEffect(() => {
    setEligiblesPage(1);
    setEligiblesSearchTerm('');
    setOperationsPage(1);
  }, [offer?._id]);

  useEffect(() => {
    handleRefreshOperations();
  }, [handleRefreshOperations]);

  useEffect(() => {
    const shouldCheckParticipation =
      Boolean(beneficiaryId) &&
      Boolean(incentiveOfferId) &&
      Boolean(resolvedSocietyId) &&
      offer?.type === IncentiveOfferType.BUY &&
      Boolean(resolvedSellerBeneficiaryId);

    if (!shouldCheckParticipation) {
      setHasParticipatedInOffer(false);
      setExistingBeneficiaryOperation(null);
      setIsLoadingParticipationCheck(false);
      return undefined;
    }

    let cancelled = false;

    const fetchParticipation = async () => {
      setIsLoadingParticipationCheck(true);

      try {
        const response = await store.dispatch(
          getIncentiveOfferOperations({
            incentiveOfferId,
            societyId: resolvedSocietyId,
            seller: resolvedSellerBeneficiaryId,
            page: 1,
            items: 1,
          })
        );

        if (cancelled) {
          return;
        }

        const hasExistingOperation = (response?.total ?? 0) > 0;
        setHasParticipatedInOffer(hasExistingOperation);
        setExistingBeneficiaryOperation(response?.data?.[0] ?? null);
      } catch {
        if (!cancelled) {
          setHasParticipatedInOffer(false);
          setExistingBeneficiaryOperation(null);
        }
      } finally {
        if (!cancelled) {
          setIsLoadingParticipationCheck(false);
        }
      }
    };

    fetchParticipation();

    return () => {
      cancelled = true;
    };
  }, [
    beneficiaryId,
    incentiveOfferId,
    resolvedSocietyId,
    offer?.type,
    resolvedSellerBeneficiaryId,
  ]);

  const contextValue = useMemo(
    () => ({
      data: {
        societyId: societyId || '',
        societyName: actualSociety?.name || '',
        offer,
        offerStatus,
        offerReference,
        acceptanceHistory,
        incentiveOfferOperations,
        priceContext,
        timelineSteps,
        sellerInfo,
        availableShares,
        totalAvailableUnits: sellerMarketContext.totalAvailableUnits,
        offerRemainingUnits,
        sellerOfferCap,
        sellerPlans,
        daysRemaining,
        isAdmin,
        isExpired,
        isOwnOffer,
        userBeneficiaryIds,
        beneficiaryQueryParam,
        isBeneficiaryView: Boolean(beneficiaryId),
        viewingBeneficiary: beneficiaryBuyer,
        isLoading:
          isLoading ||
          !actualSociety ||
          (shouldLoadSellerCap && isLoadingSellerCap) ||
          isLoadingParticipationCheck,
        hasPartnerBuyer: !!beneficiaryBuyer,
        purchaseQuantity,
        sellPlanSelection,
        commissionRate: COMMISSION_RATE,
        eligibles,
        eligiblesPage,
        eligiblesRowsPerPage,
        eligiblesTotal,
        eligiblesSearchTerm,
        isLoadingEligibles,
        isDownloadingEligibles,
        operationsPage,
        operationsRowsPerPage,
        operationsTotal,
        operationsSummary,
        isLoadingOperations,
        isDownloadingOperations,
        hasParticipatedInOffer,
        existingBeneficiaryOperation,
        isLoadingParticipationCheck,
      },
      actions: {
        handleGoBack,
        handlePurchaseQuantityChange,
        handleSellUnitsConfirm,
        handleBuyShares,
        handleViewOtherOffers,
        handleGoToOperation,
        handleOpenGenerateContractModal,
        handleEligiblesPageChange,
        handleEligiblesRowsPerPageChange,
        handleEligiblesSearchChange,
        handleDownloadEligibles,
        handleOperationsPageChange,
        handleOperationsRowsPerPageChange,
        handleDownloadOperations,
      },
    }),
    [
      societyId,
      actualSociety,
      offer,
      offerStatus,
      offerReference,
      acceptanceHistory,
      incentiveOfferOperations,
      priceContext,
      timelineSteps,
      sellerInfo,
      availableShares,
      sellerMarketContext.totalAvailableUnits,
      offerRemainingUnits,
      sellerOfferCap,
      sellerPlans,
      daysRemaining,
      isAdmin,
      isExpired,
      isOwnOffer,
      userBeneficiaryIds,
      beneficiaryQueryParam,
      beneficiaryId,
      isLoading,
      beneficiaryBuyer,
      purchaseQuantity,
      sellPlanSelection,
      shouldLoadSellerCap,
      isLoadingSellerCap,
      eligibles,
      eligiblesPage,
      eligiblesRowsPerPage,
      eligiblesTotal,
      eligiblesSearchTerm,
      isLoadingEligibles,
      isDownloadingEligibles,
      operationsPage,
      operationsRowsPerPage,
      operationsTotal,
      operationsSummary,
      isLoadingOperations,
      isDownloadingOperations,
      hasParticipatedInOffer,
      existingBeneficiaryOperation,
      isLoadingParticipationCheck,
      handleGoBack,
      handlePurchaseQuantityChange,
      handleSellUnitsConfirm,
      handleBuyShares,
      handleViewOtherOffers,
      handleGoToOperation,
      handleOpenGenerateContractModal,
      handleEligiblesPageChange,
      handleEligiblesRowsPerPageChange,
      handleEligiblesSearchChange,
      handleDownloadEligibles,
      handleOperationsPageChange,
      handleOperationsRowsPerPageChange,
      handleDownloadOperations,
    ]
  );

  return (
    <IncentiveOfferDetailContext.Provider value={contextValue}>
      {children}
    </IncentiveOfferDetailContext.Provider>
  );
};

export const useIncentiveOfferDetail = () => {
  const context = useContext(IncentiveOfferDetailContext);
  if (!context) {
    throw new Error(
      'useIncentiveOfferDetail must be used within a IncentiveOfferDetailProvider'
    );
  }
  return context;
};
